import { minesweeper, type MinesweeperRules } from '@cardengine/shared';
import { useStore } from '../store.js';

interface MinesweeperRulesEditorProps {
  rules: MinesweeperRules;
  editable: boolean;
}

/**
 * Minesweeper's settings.
 *
 * Built in the same shape as the four editors before it, down to the class
 * names, so a lobby looks like one page whatever is being played.
 *
 * The mine count is the control that has to answer to the other two — the way
 * noughts and crosses' win length answers to its grid, and rather more sharply,
 * because a board can be asked for more mines than it has squares to put them
 * in. Every control that can invalidate it therefore sends the corrected count
 * along with itself; the engine clamps regardless, and this is so the clamp
 * never has to surprise anyone.
 *
 * It is shown as a density as well as a count for a different reason: forty
 * mines means nothing on its own. "About one square in six" is the figure that
 * can be compared between two boards, and it is the one that actually predicts
 * how the game will feel.
 */
export function MinesweeperRulesEditor({ rules, editable }: MinesweeperRulesEditorProps) {
  const updateRules = useStore((s) => s.updateRules);

  const activePreset = minesweeper.RULE_PRESETS.find(
    (preset) => JSON.stringify(preset.rules) === JSON.stringify(rules),
  );

  const squares = minesweeper.cellCount(rules);
  const ceiling = minesweeper.maxMines(rules);
  const oneIn = Math.round(squares / rules.mines);

  /** Any change that can shrink the mine ceiling, sent with a count that fits. */
  const reshape = (next: Partial<MinesweeperRules>) => {
    if (!editable) return;
    const board = { ...rules, ...next };
    updateRules({ ...next, mines: Math.min(rules.mines, minesweeper.maxMines(board)) });
  };

  return (
    <section className="rules" aria-label="Minesweeper settings">
      <header className="rules__header">
        <h2>Settings</h2>
      </header>

      <div className="presets">
        <span className="presets__label">Preset</span>
        <div className="presets__row">
          {minesweeper.RULE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`presets__button ${activePreset?.id === preset.id ? 'is-active' : ''}`}
              disabled={!editable}
              onClick={() => editable && updateRules(preset.rules)}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>
        <p className="presets__description">
          {activePreset ? activePreset.description : 'A board of your own.'}
        </p>
      </div>

      <div className="rules__ranks">
        <label className="rule rule--choice">
          <span className="rule__label">Across</span>
          <input
            type="number"
            min={minesweeper.MIN_WIDTH}
            max={minesweeper.MAX_WIDTH}
            value={rules.width}
            disabled={!editable}
            onChange={(event) => reshape({ width: Number(event.target.value) })}
          />
          <span className="rule__hint">
            {minesweeper.MIN_WIDTH} to {minesweeper.MAX_WIDTH} squares wide.
          </span>
        </label>

        <label className="rule rule--choice">
          <span className="rule__label">Down</span>
          <input
            type="number"
            min={minesweeper.MIN_HEIGHT}
            max={minesweeper.MAX_HEIGHT}
            value={rules.height}
            disabled={!editable}
            onChange={(event) => reshape({ height: Number(event.target.value) })}
          />
          <span className="rule__hint">
            {minesweeper.MIN_HEIGHT} to {minesweeper.MAX_HEIGHT} squares tall.
          </span>
        </label>

        <label className="rule rule--choice">
          <span className="rule__label">Mines</span>
          <input
            type="number"
            min={minesweeper.MIN_MINES}
            max={ceiling}
            value={rules.mines}
            disabled={!editable}
            onChange={(event) => editable && updateRules({ mines: Number(event.target.value) })}
          />
          <span className="rule__hint">
            {squares} squares, about one in {oneIn} mined. {ceiling} is as many as this board holds.
          </span>
        </label>
      </div>

      <div className="rules__toggles">
        <label className={`rule ${!editable ? 'rule--locked' : ''}`}>
          <input
            type="checkbox"
            checked={rules.safeOpening}
            disabled={!editable}
            // Turning it on takes nine squares out of the pool the mines are
            // drawn from, so it can put the count over what the board holds.
            onChange={(event) => reshape({ safeOpening: event.target.checked })}
          />
          <span className="rule__body">
            <span className="rule__label">First square opens a region</span>
            <span className="rule__hint">
              The mines are laid around wherever you open first, so it is always blank and always gives
              you something to read. Off, only that one square is safe — the original behaviour, and a
              first move that can leave you staring at a lone number with nothing to go on.
            </span>
          </span>
        </label>

        <label className={`rule ${!editable ? 'rule--locked' : ''}`}>
          <input
            type="checkbox"
            checked={rules.flagsLimited}
            disabled={!editable}
            onChange={(event) => editable && updateRules({ flagsLimited: event.target.checked })}
          />
          <span className="rule__body">
            <span className="rule__label">Only as many flags as mines</span>
            <span className="rule__hint">
              Running out tells you one of your flags is wrong, which is a real hint and half the
              reason the counter exists. Off, it can go below zero and flags become notes to
              yourself — useful for marking "one of these two" without deciding which.
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}
